import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getHowlInstance, updateGlobalVolume } from "./useAudioPreload";
import { useAudioInteraction } from "../context/AudioInteractionContext";

const useNarrationsNew = ({
  storageKey,
  playOnce = true,
  onStart = () => {},
  onEnd = () => {},
  onStep = () => {},
} = {}) => {
  const currentSoundRef = useRef(null);
  const initialDelayTimeoutRef = useRef(null);
  const stepDelayTimeoutRef = useRef(null);

  const sessionIdRef = useRef(0);
  const isSkippingRef = useRef(false);

  // IMPORTANT: this ref is the real source of truth for "playing"
  const isPlayingRef = useRef(false);

  const onStartRef = useRef(onStart);
  const onEndRef = useRef(onEnd);
  const onStepRef = useRef(onStep);

  useEffect(() => {
    onStartRef.current = onStart;
  }, [onStart]);
  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);
  useEffect(() => {
    onStepRef.current = onStep;
  }, [onStep]);

  const { masterVolume } = useSelector((s) => s.settings);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);

  const { ensureAudioReady } = useAudioInteraction();

  // Keep state + ref in sync, but ref is updated FIRST for immediate correctness
  const setPlaying = useCallback((val) => {
    isPlayingRef.current = !!val;
    setIsPlaying(!!val);
  }, []);

  useEffect(() => {
    const vol = (Number(masterVolume) || 0) / 100;
    updateGlobalVolume(vol);
  }, [masterVolume]);

  const clearAllDelays = useCallback(() => {
    if (initialDelayTimeoutRef.current) {
      clearTimeout(initialDelayTimeoutRef.current);
      initialDelayTimeoutRef.current = null;
    }
    if (stepDelayTimeoutRef.current) {
      clearTimeout(stepDelayTimeoutRef.current);
      stepDelayTimeoutRef.current = null;
    }
  }, []);

  const ensureLoaded = useCallback((howl) => {
    return new Promise((resolve, reject) => {
      if (!howl) return reject(new Error("No Howl instance"));
      if (howl.state() === "loaded") return resolve();

      const onLoad = () => {
        howl.off("load", onLoad);
        howl.off("loaderror", onError);
        resolve();
      };

      const onError = (_id, err) => {
        howl.off("load", onLoad);
        howl.off("loaderror", onError);
        reject(err || new Error("loaderror"));
      };

      howl.once("load", onLoad);
      howl.once("loaderror", onError);
    });
  }, []);

  const safeStopCurrent = useCallback(() => {
    try {
      if (currentSoundRef.current) {
        const { howl, soundId } = currentSoundRef.current;
        howl.stop(soundId);
      }
    } catch (e) {
      console.warn("[Narrations] Error stopping current sound:", e);
    }
    currentSoundRef.current = null;
  }, []);

  const stop = useCallback(() => {
    sessionIdRef.current += 1; // cancel any in-flight async work
    clearAllDelays();
    safeStopCurrent();
    setPlaying(false);
    setCurrentStep(-1);
    isSkippingRef.current = false;
  }, [clearAllDelays, safeStopCurrent, setPlaying]);

  const skip = useCallback(() => {
    isSkippingRef.current = true;

    sessionIdRef.current += 1;
    clearAllDelays();
    safeStopCurrent();

    setPlaying(false);
    setCurrentStep(-1);

    if (storageKey) {
      try {
        localStorage.setItem(storageKey, "1");
      } catch (e) {
        console.warn("[Narrations] Failed to save to localStorage:", e);
      }
    }

    try {
      onEndRef.current?.();
    } catch (e) {
      console.warn("[Narrations] Error in onEnd callback:", e);
    }

    isSkippingRef.current = false;
  }, [storageKey, clearAllDelays, safeStopCurrent, setPlaying]);

  const hasPlayed = useCallback(() => {
    if (!storageKey) return false;
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  }, [storageKey]);

  const playNextWithGap = useCallback((fn, gapMs) => {
    stepDelayTimeoutRef.current = setTimeout(fn, gapMs ?? 50);
  }, []);

  const playSequence = useCallback(
    async (sequence, delay = 100, options = {}) => {
      if (!Array.isArray(sequence) || sequence.length === 0) {
        console.warn("[Narrations] Invalid sequence provided");
        return;
      }

      const ok = await ensureAudioReady?.();
      if (!ok) return;

      if (playOnce && storageKey && hasPlayed()) return;

      // cancel any existing playback/session
      stop();

      const mySessionId = ++sessionIdRef.current;

      const advance = async (index) => {
        if (mySessionId !== sessionIdRef.current) return;

        if (index >= sequence.length) {
          setPlaying(false);
          setCurrentStep(-1);
          currentSoundRef.current = null;

          if (storageKey && !isSkippingRef.current) {
            try {
              localStorage.setItem(storageKey, "1");
            } catch (e) {
              console.warn("[Narrations] Failed to save to localStorage:", e);
            }
          }

          if (!isSkippingRef.current) {
            try {
              onEndRef.current?.();
            } catch (e) {
              console.warn("[Narrations] Error in onEnd callback:", e);
            }
          }
          return;
        }

        const key = sequence[index];
        setCurrentStep(index);

        try {
          onStepRef.current?.(key, index);
          if (typeof options.onStep === "function") options.onStep(key, index);
        } catch (e) {
          console.warn("[Narrations] Error in onStep callback:", e);
        }

        const howl = getHowlInstance(key, (Number(masterVolume) || 0) / 100);
        if (!howl) {
          console.warn(`[Narrations] No howl for "${key}"`);
          return playNextWithGap(() => advance(index + 1), options.stepDelay);
        }

        try {
          await ensureLoaded(howl);
        } catch (e) {
          console.warn(`[Narrations] loaderror for "${key}"`, e);
          return playNextWithGap(() => advance(index + 1), options.stepDelay);
        }

        if (mySessionId !== sessionIdRef.current) return;

        let soundId;
        try {
          soundId = howl.play();
          if (soundId == null) {
            console.warn(`[Narrations] play() failed for "${key}"`);
            return playNextWithGap(() => advance(index + 1), options.stepDelay);
          }
        } catch (e) {
          console.warn(`[Narrations] playerror for "${key}"`, e);
          return playNextWithGap(() => advance(index + 1), options.stepDelay);
        }

        currentSoundRef.current = { howl, soundId };

        const onPlayError = () => {
          howl.off("playerror", onPlayError, soundId);
          if (mySessionId !== sessionIdRef.current) return;
          playNextWithGap(() => advance(index + 1), options.stepDelay);
        };

        const handleEndedLocal = () => {
          howl.off("end", handleEndedLocal, soundId);
          howl.off("playerror", onPlayError, soundId);
          if (mySessionId !== sessionIdRef.current) return;
          playNextWithGap(() => advance(index + 1), options.stepDelay);
        };

        howl.once("playerror", onPlayError, soundId);
        howl.once("end", handleEndedLocal, soundId);
      };

      setPlaying(true);
      setCurrentStep(-1);

      try {
        onStartRef.current?.();
      } catch (e) {
        console.warn("[Narrations] Error in onStart callback:", e);
      }

      clearAllDelays();
      initialDelayTimeoutRef.current = setTimeout(() => {
        if (mySessionId === sessionIdRef.current) advance(0);
      }, delay);
    },
    [
      ensureAudioReady,
      playOnce,
      storageKey,
      hasPlayed,
      ensureLoaded,
      masterVolume,
      clearAllDelays,
      playNextWithGap,
      stop,
      setPlaying,
    ]
  );

  const playSprite = useCallback(
    async (spriteKey) => {
      if (playOnce && storageKey && hasPlayed()) return;

      const ok = await ensureAudioReady?.();
      if (!ok) return;

      // Synchronous guard (fixes your warning + race)
      if (isPlayingRef.current) {
        console.warn("[Narrations] Already playing, ignoring playSprite call");
        return;
      }

      // Create new session & mark playing immediately
      const mySessionId = ++sessionIdRef.current;
      setPlaying(true);
      setCurrentStep(-1);

      const howl = getHowlInstance(
        spriteKey,
        (Number(masterVolume) || 0) / 100
      );

      if (!howl) {
        console.warn(`[Narrations] No howl for "${spriteKey}"`);
        if (mySessionId === sessionIdRef.current) setPlaying(false);
        return;
      }

      try {
        await ensureLoaded(howl);
      } catch (e) {
        console.warn(`[Narrations] loaderror for "${spriteKey}"`, e);
        if (mySessionId === sessionIdRef.current) setPlaying(false);
        return;
      }

      if (mySessionId !== sessionIdRef.current) {
        // stopped/skipped while loading
        setPlaying(false);
        return;
      }

      let soundId;
      try {
        soundId = howl.play();
        if (soundId == null) throw new Error("play() returned null");
      } catch (e) {
        console.warn(`[Narrations] playerror for "${spriteKey}"`, e);
        if (mySessionId === sessionIdRef.current) setPlaying(false);
        return;
      }

      try {
        onStartRef.current?.();
      } catch (e) {
        console.warn("[Narrations] Error in onStart callback:", e);
      }

      currentSoundRef.current = { howl, soundId };

      const handleEndOnce = () => {
        howl.off("end", handleEndOnce, soundId);
        if (mySessionId !== sessionIdRef.current) return;

        currentSoundRef.current = null;
        setPlaying(false);
        setCurrentStep(-1);

        if (storageKey && !isSkippingRef.current) {
          try {
            localStorage.setItem(storageKey, "1");
          } catch (e) {
            console.warn("[Narrations] Failed to save to localStorage:", e);
          }
        }

        if (!isSkippingRef.current) {
          try {
            onEndRef.current?.();
          } catch (e) {
            console.warn("[Narrations] Error in onEnd callback:", e);
          }
        }
      };

      howl.once("end", handleEndOnce, soundId);
    },
    [
      ensureAudioReady,
      playOnce,
      storageKey,
      hasPlayed,
      ensureLoaded,
      masterVolume,
      setPlaying,
    ]
  );

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    playSequence,
    playSprite,
    stop,
    skip,
    hasPlayed,
    isPlaying,
    currentStep,
  };
};

export default useNarrationsNew;
